import { createSelector } from 'reselect';
import {
  compose,
  find,
  propEq,
  intersection,
  filter,
  pluck,
  partition,
  prop,
  sort,
  isNil,
  identity,
  contains,
  reverse,
  length,
  without,
  difference,
} from 'ramda';
import { Compare } from '../constants';
import { isPresent, sortTypeAware } from '../utils/helpers';
import {
  getCompare,
  getSelectedLocationsIds,
  getSortColumn,
  getSortReversed,
} from './options';
import {
  getCrimes,
  getPopulationData,
  getSingleValueTotal,
  getSingleValueRate,
  getTimeIntervals,
  getTimeIntervalOnePopulation,
  getTimeIntervalTwoPopulation,
  getTimeIntervalOneCrimes,
  getTimeIntervalTwoCrimes,
  getTimeIntervalOneRate,
  getTimeIntervalTwoRate,
} from './crimes';
import {
  getPopulationSetting,
  populationPredicates,
} from '../reducers/population';
import { getCities } from '../reducers/cities';
import { getCensusCrimes } from '../reducers/crimes';
import { isCensusView, getSelectedCityId } from './router';

export const getAllCitiesIds = createSelector(getCities, pluck('id'));

export const getCensusTracts = createSelector(getCensusCrimes, crimes =>
  Object.keys(crimes).map(id => ({ id, name: id })),
);

export const getAllCensusTractsIds = createSelector(
  getCensusTracts,
  pluck('id'),
);

/**
 * Returns cities or census tract array based on current map view
 */
export const getLocations = createSelector(
  isCensusView,
  getCities,
  getCensusTracts,
  (censusView, cities, censusTracts) => (censusView ? censusTracts : cities),
);

const getPopulationFilter = (setting, populations) =>
  populationPredicates[setting](populations);

/**
 * Returns subset of all locations based on active population filter
 * For example cities with more than a million people
 */
export const getLocationsSubsetByPopulation = createSelector(
  isCensusView,
  getCities,
  getCensusTracts,
  getPopulationData,
  getPopulationSetting,
  (censusView, cities, censusTracts, populations, populationSetting) =>
    censusView
      ? censusTracts
      : cities.filter(({ id }) =>
          getPopulationFilter(populationSetting, populations[id]),
        ),
);

/**
 * Returns selected locations in current population subset
 */
export const getSelectedLocationsInPopulationSubset = createSelector(
  getLocationsSubsetByPopulation,
  getSelectedLocationsIds,
  (subset, selectedIds) => intersection(pluck('id', subset), selectedIds),
);

/**
 * Returns list of city objects for rendering in LocationTable
 */
export const getLocationsTable = createSelector(
  getLocationsSubsetByPopulation,
  getPopulationData,
  getSingleValueTotal,
  getSingleValueRate,
  getTimeIntervals,
  (locations, populations, totals, rates, timespans) =>
    locations.map(({ id, name }) => ({
      id,
      name,
      population: populations[id],
      rate: rates[id],
      total: totals[id],
      timespan: timespans[id],
    })),
);

/**
 * Returns list of city objects for rendering in LocationTable
 */
export const getCompareTable = createSelector(
  getLocationsSubsetByPopulation,
  getPopulationData,
  getSingleValueTotal,
  getSingleValueRate,
  getTimeIntervals,
  getTimeIntervalOnePopulation,
  getTimeIntervalTwoPopulation,
  getTimeIntervalOneCrimes,
  getTimeIntervalTwoCrimes,
  getTimeIntervalOneRate,
  getTimeIntervalTwoRate,
  (
    locations,
    populations,
    totals,
    rates,
    timespans,
    popOne,
    popTwo,
    crimeOne,
    crimeTwo,
    rateOne,
    rateTwo,
  ) =>
    locations.map(({ id, name }) => ({
      id,
      name,
      population: populations[id],
      rate: rates[id],
      total: totals[id],
      timespan: timespans[id],
      firstIntervalPopulation: popOne[id],
      secondIntervalPopulation: popTwo[id],
      firstIntervalMurders: crimeOne[id],
      secondIntervalMurders: crimeTwo[id],
      firstIntervalRate: rateOne[id],
      secondIntervalRate: rateTwo[id],
    })),
);

export const getLocationsOrCompareTable = createSelector(
  getCompare,
  getLocationsTable,
  getCompareTable,
  (compare, locationsTable, compareTable) => {
    return [Compare.YEAR, Compare.PERIOD, Compare.CUSTOM].indexOf(compare) >= 0
      ? compareTable
      : locationsTable;
  },
);

/**
 * List of location ids sorted by one of table columns
 */
export const getLocationsSortedIds = createSelector(
  getLocationsOrCompareTable,
  getSortColumn,
  getSortReversed,
  getSelectedLocationsIds,
  (table, sortColumn, sortReversed, selectedIds) => {
    const [nil, normal] = partition(compose(isNil, prop(sortColumn)), table);
    const normalSorted = compose(
      sortReversed ? reverse : identity,
      sort((a, b) => sortTypeAware(a[sortColumn], b[sortColumn])),
    )(normal);

    const sorted = [...pluck('id', normalSorted), ...pluck('id', nil)];
    const unselected = without(selectedIds, sorted);
    return [...difference(sorted, unselected), ...unselected];
  },
);

/**
 * Actual table data sorted
 */
export const getLocationsTableSorted = createSelector(
  getLocationsSortedIds,
  getLocationsOrCompareTable,
  (sorted, table) => sorted.map(id => table.find(row => id === row.id)),
);

/**
 * Returns a list of location ids for which we have any data
 */
export const getLocationsWithDataIds = createSelector(
  getLocations,
  getCrimes,
  (locations, crimes) =>
    compose(pluck('id'), filter(({ id }) => isPresent(crimes[id])))(locations),
);

/**
 * Returns a list of location ids for which we have two or more data points
 */
export const getLocationsWithMultipleDataIds = createSelector(
  getLocations,
  getCrimes,
  (locations, crimes) =>
    compose(
      pluck('id'),
      filter(({ id }) => crimes[id] && crimes[id].length >= 2),
    )(locations),
);

/**
 * Return a list of selected and sorted location ids
 */
export const getSortedSelectedWithDataIds = createSelector(
  getLocationsWithDataIds,
  getSelectedLocationsIds,
  getLocationsSortedIds,
  (withData, selected, sorted) => {
    const validIds = intersection(selected, withData);
    return sorted.filter(id => contains(id, validIds));
  },
);

export const getFilteredLocationsCount = createSelector(
  getSortedSelectedWithDataIds,
  length,
);

export const getTotalLocationsCount = createSelector(
  getLocations,
  getLocationsWithDataIds,
  (locations, withData) =>
    locations.filter(({ id }) => withData.indexOf(id) >= 0).length,
);

export const getCityNameById = (state, id) =>
  id ? compose(prop('name'), find(propEq('id', id)), getCities)(state) : null;

export const getSelectedCityName = state =>
  getCityNameById(state, getSelectedCityId(state));



// WEBPACK FOOTER //
// ./src/selectors/locations.js